import Navbar from "./components/Navbar";
import FooterSection from "./components/FooterSection";

export default function NewFourDataLayerPage() {
  return (
    <div className="min-h-screen bg-[#fff8ef] text-[#171717] overflow-hidden">
      <div className="fixed inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(circle at 12% 18%, rgba(255,126,78,0.22), transparent 18%), radial-gradient(circle at 82% 14%, rgba(89,170,255,0.22), transparent 20%), radial-gradient(circle at 74% 68%, rgba(102,238,201,0.18), transparent 18%)" }} />
      <Navbar />
      <main className="relative z-10 pt-[110px] pb-20">
        <section className="pb-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="rounded-[34px] border border-[#171717]/10 bg-[linear-gradient(135deg,rgba(232,255,247,0.94),rgba(255,255,255,0.84))] p-8 lg:p-12 shadow-[0_18px_50px_rgba(23,23,23,0.05)]">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#6b6b6b] font-bold mb-4">Service • AI Data Layer</p>
              <div className="grid lg:grid-cols-[1fr_0.9fr] gap-8 items-start">
                <div>
                  <h1 className="text-5xl md:text-6xl font-black tracking-[-0.06em] leading-[0.94] mb-5">Build the machine-readable layer underneath visibility, conversion, and AI logic.</h1>
                  <p className="text-[#4f4f4f] text-lg leading-relaxed max-w-3xl">The AI Data Layer turns fragmented product, category, and entity information into a cleaner commercial structure. It is what helps the rest of the system stop guessing.</p>
                </div>
                <div className="rounded-[24px] border border-[#171717]/10 bg-white/72 p-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-2">Why structure changes everything downstream</p>
                  <p className="text-sm text-[#4f4f4f] leading-relaxed">The companies that win with AI usually do not have more data. They have cleaner structure, better signal quality, and less ambiguity between what the business is and how the site describes it.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 space-y-4">
            {[
              ["01", "Source normalization", "Bring catalog, CMS, and page-level fields into one cleaner logic layer."],
              ["02", "Entity structure", "Make products, categories, services, and brand attributes easier for machines to interpret."],
              ["03", "Schema implementation", "Translate the right parts of the business into structured, usable signals."],
              ["04", "System reinforcement", "Feed stronger source material into GEO, content, CRO, and reporting layers."],
            ].map(([step, title, text], idx) => (
              <div key={step} className={`grid md:grid-cols-[0.16fr_0.34fr_0.5fr] gap-4 items-center rounded-[28px] border border-[#171717]/10 p-5 shadow-[0_18px_50px_rgba(23,23,23,0.05)] ${idx % 2 === 0 ? "bg-white/84" : "bg-[#e8fff7]"}`}>
                <div className="text-4xl font-black tracking-[-0.06em] text-[#171717]">{step}</div>
                <div className="text-2xl font-black tracking-[-0.04em]">{title}</div>
                <div className="text-[#4f4f4f] leading-relaxed">{text}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1fr] gap-6">
            <div className="rounded-[30px] border border-[#171717]/10 bg-[#171717] text-[#fff8ef] p-7 shadow-[0_18px_50px_rgba(23,23,23,0.12)]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">From source structure to AI visibility</p>
              <div className="space-y-3">
                {[
                  "Catalog logic",
                  "Category relationships",
                  "Product attributes",
                  "Schema layer",
                  "Visibility systems",
                ].map((item, idx) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-10 text-sm font-black text-[#59aaff]">0{idx + 1}</div>
                    <div className="flex-1 h-12 rounded-[16px] bg-white/8 border border-white/10 px-4 flex items-center justify-between">
                      <span className="text-sm text-white/80">{item}</span>
                      <i className="ri-arrow-right-line text-[#ffd14f]"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-4">
              <div className="rounded-[28px] border border-[#171717]/10 bg-white/84 p-6 rotate-[-1deg]"><h3 className="text-2xl font-black mb-3">Why brands get stuck</h3><p className="text-[#4f4f4f] leading-relaxed">Most businesses do not lack data. They lack one coherent structure that AI can trust across page templates, feeds, attributes, and category systems.</p></div>
              <div className="rounded-[28px] border border-[#171717]/10 bg-[#dff3ff] p-6 rotate-[1deg]"><h3 className="text-2xl font-black mb-3">What Flowvory improves</h3><p className="text-[#4f4f4f] leading-relaxed">We make the information layer cleaner so discovery, comparison, personalization, and reporting all have better raw material to work from.</p></div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.05fr_0.95fr] gap-6 items-start">
            <div className="grid gap-5">
              <div className="rounded-[30px] border border-[#171717]/10 bg-white/86 p-6 shadow-[0_18px_50px_rgba(23,23,23,0.05)]">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-3">How much of the commercial structure is explicit, not implied</p>
                <p className="text-sm text-[#4f4f4f] leading-relaxed mb-5">These percentages are not performance claims. They show the four parts of the data layer that usually determine whether AI systems can interpret the business with clarity.</p>
                <div className="space-y-5">
                  {[
                    ["Product logic", 86, "#59aaff"],
                    ["Category relationships", 74, "#66eec9"],
                    ["Attribute coverage", 68, "#ffd14f"],
                    ["Schema readiness", 82, "#ff7e4e"],
                  ].map(([label, value, color]) => (
                    <div key={label as string}>
                      <div className="flex items-center justify-between mb-2 text-sm">
                        <span className="font-semibold text-[#171717]">{label as string}</span>
                        <span className="font-black text-[#171717]">{value as number}%</span>
                      </div>
                      <div className="h-4 rounded-full bg-[#171717]/8 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color as string }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-[30px] border border-[#171717]/10 bg-[#f8f8f5] p-6 shadow-[0_18px_50px_rgba(23,23,23,0.05)]">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-3">The questions a weak data layer usually cannot answer cleanly</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    "Can AI read the offer without guessing what matters most?",
                    "Do related pages describe the same category logic consistently?",
                    "Are the most useful attributes structured and reusable?",
                    "Can downstream systems work from the same source of truth?",
                  ].map((item, idx) => (
                    <div key={item} className={`rounded-[18px] border border-[#171717]/10 px-4 py-3 text-sm font-semibold leading-relaxed ${idx % 2 === 0 ? "bg-[#dff3ff]" : "bg-[#fff0db]"}`}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-[34px] border border-[#171717]/10 bg-white/84 p-8 flex flex-col items-start justify-between gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#6b6b6b] font-bold mb-3">Flowvory advantage</p>
              <p className="text-2xl font-black tracking-[-0.04em] max-w-3xl">This is not abstract schema theatre. It is commercial infrastructure that makes the rest of the growth system less fragile.</p>
            </div>
              <div className="w-full rounded-[24px] border border-[#171717]/10 bg-[#f8f8f5] p-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-2">Why this work is more commercial than it looks</p>
                <p className="text-sm text-[#4f4f4f] leading-relaxed">We do not treat the data layer as a technical appendix. We treat it as the part of the business that determines how discoverable, understandable, and optimizable the rest of the system becomes.</p>
              </div>
              <button onClick={() => window.dispatchEvent(new Event("flowvory:open-strategy"))} className="px-6 py-3 rounded-full bg-[#171717] text-[#fff8ef] font-black text-sm shadow-[6px_6px_0_rgba(89,170,255,0.16)]">Book a Call</button>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
